/**
 * Native LiDAR Bridge - Full Mobile Integration
 * 
 * Complete native mobile integration for iOS and Android devices:
 * 
 * 1. iOS Native Bridge (Swift/WKWebView)
 *    - RoomPlan API (iOS 16+ with LiDAR)
 *    - ARKit LiDAR scanning
 *    - Native message handlers
 * 
 * 2. Android Native Bridge (Kotlin/WebView)
 *    - ARCore Depth API
 *    - Native JavaScript interface
 * 
 * 3. Web Fallback
 *    - Photogrammetry multi-photo capture
 *    - WebXR AR guidance (where supported)
 * 
 * Protocol:
 * - iOS: window.webkit.messageHandlers.letwiseLiDAR.postMessage()
 * - Android: window.LetWiseAndroid.methodName()
 * - Web: Photogrammetry with server-side processing
 */

// =============================================================================
// NATIVE PROTOCOL DEFINITIONS
// =============================================================================

/**
 * Native message protocol for iOS WKWebView
 */
const IOS_PROTOCOL = {
  HANDLER_NAME: 'letwiseLiDAR',
  MESSAGES: {
    INITIALIZE: 'initialize',
    CHECK_CAPABILITIES: 'checkCapabilities',
    START_ROOMPLAN: 'startRoomPlan',
    START_LIDAR_SCAN: 'startLiDARScan',
    CAPTURE_FRAME: 'captureFrame',
    STOP_SCAN: 'stopScan',
    EXPORT_USDZ: 'exportUSDZ',
    EXPORT_MESH: 'exportMesh',
    GET_PROGRESS: 'getProgress',
    CANCEL: 'cancel',
  },
  CALLBACKS: {
    ON_INITIALIZED: 'onLiDARInitialized',
    ON_PROGRESS: 'onScanProgress',
    ON_FRAME: 'onFrameCaptured',
    ON_COMPLETE: 'onScanComplete',
    ON_ERROR: 'onScanError',
    ON_ROOM_DETECTED: 'onRoomDetected',
    ON_OBJECT_DETECTED: 'onObjectDetected',
  }
};

/**
 * Native message protocol for Android WebView
 */
const ANDROID_PROTOCOL = {
  INTERFACE_NAME: 'LetWiseAndroid',
  METHODS: {
    INITIALIZE: 'initializeLiDAR',
    CHECK_CAPABILITIES: 'checkDeviceCapabilities',
    START_DEPTH_SCAN: 'startDepthScan',
    START_AR_SESSION: 'startARSession',
    CAPTURE_DEPTH_FRAME: 'captureDepthFrame',
    STOP_SCAN: 'stopScan',
    EXPORT_MESH: 'exportMesh',
    GET_PROGRESS: 'getProgress',
    CANCEL: 'cancelScan',
  }
};


// =============================================================================
// DEVICE CAPABILITY DETECTION
// =============================================================================

export const DeviceCapabilities = {
  /**
   * Comprehensive device capability check
   */
  async getFullCapabilities() {
    const platform = this.detectPlatform();
    const capabilities = {
      platform,
      hasLiDAR: false,
      hasRoomPlan: false,
      hasARCoreDepth: false,
      hasWebXR: false,
      hasCamera: false,
      hasNativeBridge: false,
      nativeBridgeVersion: null,
      supportedFormats: ['photos'],
      recommended3DMode: 'photo_photogrammetry',
      maxResolution: 'standard',
      supportsRealtime: false,
    };

    // Check native iOS bridge
    if (platform.os === 'ios') {
      capabilities.hasNativeBridge = this._checkIOSBridge();
      if (capabilities.hasNativeBridge) {
        try {
          const nativeCaps = await this._queryIOSCapabilities();
          capabilities.hasLiDAR = nativeCaps.hasLiDAR || false;
          capabilities.hasRoomPlan = nativeCaps.hasRoomPlan || false;
          capabilities.nativeBridgeVersion = nativeCaps.version;
          capabilities.supportedFormats = nativeCaps.formats || ['usdz', 'obj', 'photos'];
          capabilities.maxResolution = nativeCaps.maxResolution || 'high';
          capabilities.supportsRealtime = nativeCaps.supportsRealtime || false;
        } catch (e) {
          console.warn('iOS capability query failed:', e);
        }
      }
    }

    // Check native Android bridge
    if (platform.os === 'android') {
      capabilities.hasNativeBridge = this._checkAndroidBridge();
      if (capabilities.hasNativeBridge) {
        try {
          const nativeCaps = await this._queryAndroidCapabilities();
          capabilities.hasARCoreDepth = nativeCaps.hasDepthAPI || false;
          capabilities.nativeBridgeVersion = nativeCaps.version;
          capabilities.supportedFormats = nativeCaps.formats || ['obj', 'ply', 'photos'];
        } catch (e) {
          console.warn('Android capability query failed:', e);
        }
      }
    }

    // Check WebXR
    capabilities.hasWebXR = await this._checkWebXR();

    // Check camera
    capabilities.hasCamera = await this._checkCamera();

    // Determine recommended mode
    if (capabilities.hasRoomPlan && capabilities.hasLiDAR) {
      capabilities.recommended3DMode = 'roomplan';
    } else if (capabilities.hasLiDAR) {
      capabilities.recommended3DMode = 'lidar_native';
    } else if (capabilities.hasARCoreDepth) {
      capabilities.recommended3DMode = 'arcore_depth';
    } else if (capabilities.hasWebXR) {
      capabilities.recommended3DMode = 'webxr_guided';
    } else if (capabilities.hasCamera) {
      capabilities.recommended3DMode = 'photo_photogrammetry';
    }

    return capabilities;
  },

  detectPlatform() {
    const ua = navigator.userAgent;
    
    if (/iPad|iPhone|iPod/.test(ua)) {
      const match = ua.match(/OS (\d+)_(\d+)/);
      return {
        os: 'ios',
        version: match ? parseFloat(`${match[1]}.${match[2]}`) : 0,
        isNativeApp: !!window.webkit?.messageHandlers?.[IOS_PROTOCOL.HANDLER_NAME],
        deviceModel: this._getIOSDeviceModel(),
      };
    }
    
    if (/Android/.test(ua)) {
      const match = ua.match(/Android (\d+)/);
      return {
        os: 'android',
        version: match ? parseInt(match[1]) : 0,
        isNativeApp: !!window[ANDROID_PROTOCOL.INTERFACE_NAME],
        deviceModel: this._getAndroidDeviceModel(),
      };
    }
    
    return {
      os: 'web',
      version: 0,
      isNativeApp: false,
      deviceModel: 'browser',
    };
  },

  _checkIOSBridge() {
    return !!(
      window.webkit?.messageHandlers?.[IOS_PROTOCOL.HANDLER_NAME] ||
      window.LetWiseLiDARBridge
    );
  },

  _checkAndroidBridge() {
    return !!(window[ANDROID_PROTOCOL.INTERFACE_NAME]);
  },

  async _queryIOSCapabilities() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Capability query timeout'));
      }, 3000);

      window._lidarCapabilityCallback = (caps) => {
        clearTimeout(timeout);
        resolve(caps);
        delete window._lidarCapabilityCallback;
      };

      if (window.webkit?.messageHandlers?.[IOS_PROTOCOL.HANDLER_NAME]) {
        window.webkit.messageHandlers[IOS_PROTOCOL.HANDLER_NAME].postMessage({
          action: IOS_PROTOCOL.MESSAGES.CHECK_CAPABILITIES,
          callbackId: '_lidarCapabilityCallback',
        });
      } else if (window.LetWiseLiDARBridge?.checkCapabilities) {
        window.LetWiseLiDARBridge.checkCapabilities().then(resolve).catch(reject);
      } else {
        resolve({});
      }
    });
  },

  async _queryAndroidCapabilities() {
    return new Promise((resolve) => {
      if (window[ANDROID_PROTOCOL.INTERFACE_NAME]?.[ANDROID_PROTOCOL.METHODS.CHECK_CAPABILITIES]) {
        try {
          const result = window[ANDROID_PROTOCOL.INTERFACE_NAME][ANDROID_PROTOCOL.METHODS.CHECK_CAPABILITIES]();
          resolve(JSON.parse(result));
        } catch (e) {
          resolve({});
        }
      } else {
        resolve({});
      }
    });
  },

  async _checkWebXR() {
    if (!navigator.xr) return false;
    try {
      return await navigator.xr.isSessionSupported('immersive-ar');
    } catch {
      return false;
    }
  },

  async _checkCamera() {
    if (!navigator.mediaDevices?.getUserMedia) return false;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some(d => d.kind === 'videoinput');
    } catch {
      return false;
    }
  },

  _getIOSDeviceModel() {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) {
      // Check for Pro models (have LiDAR)
      if (/iPhone1[2-9],\d+|iPhone\d{2},\d+/.test(ua)) {
        return 'iPhone Pro';
      }
      return 'iPhone';
    }
    if (/iPad/.test(ua)) {
      return 'iPad';
    }
    return 'iOS Device';
  },

  _getAndroidDeviceModel() {
    const ua = navigator.userAgent;
    const match = ua.match(/\(Linux;.*?;\s*([^)]+)\)/);
    return match ? match[1].trim() : 'Android Device';
  },
};


// =============================================================================
// IOS NATIVE LIDAR BRIDGE
// =============================================================================

export class IOSLiDARBridge {
  constructor() {
    this.sessionId = null;
    this.callbacks = {};
    this.isScanning = false;
    this._setupCallbacks();
  }

  _setupCallbacks() {
    // Setup global callbacks for iOS native layer
    window[IOS_PROTOCOL.CALLBACKS.ON_INITIALIZED] = (data) => {
      this.callbacks.onInitialized?.(data);
    };

    window[IOS_PROTOCOL.CALLBACKS.ON_PROGRESS] = (data) => {
      this.callbacks.onProgress?.(data);
    };

    window[IOS_PROTOCOL.CALLBACKS.ON_FRAME] = (data) => {
      this.callbacks.onFrame?.(data);
    };

    window[IOS_PROTOCOL.CALLBACKS.ON_COMPLETE] = (data) => {
      this.isScanning = false;
      this.callbacks.onComplete?.(data);
    };

    window[IOS_PROTOCOL.CALLBACKS.ON_ERROR] = (data) => {
      this.isScanning = false;
      this.callbacks.onError?.(data);
    };

    window[IOS_PROTOCOL.CALLBACKS.ON_ROOM_DETECTED] = (data) => {
      this.callbacks.onRoomDetected?.(data);
    };

    window[IOS_PROTOCOL.CALLBACKS.ON_OBJECT_DETECTED] = (data) => {
      this.callbacks.onObjectDetected?.(data);
    };
  }

  _postMessage(action, payload = {}) {
    const message = { action, ...payload, sessionId: this.sessionId };
    
    if (window.webkit?.messageHandlers?.[IOS_PROTOCOL.HANDLER_NAME]) {
      window.webkit.messageHandlers[IOS_PROTOCOL.HANDLER_NAME].postMessage(message);
      return true;
    }
    
    if (window.LetWiseLiDARBridge?.[action]) {
      window.LetWiseLiDARBridge[action](payload);
      return true;
    }
    
    return false;
  }

  async initialize() {
    return new Promise((resolve) => {
      this.callbacks.onInitialized = (data) => {
        resolve({ success: true, ...data });
      };

      const sent = this._postMessage(IOS_PROTOCOL.MESSAGES.INITIALIZE);
      if (!sent) {
        resolve({ success: false, error: 'iOS bridge not available' });
      }
    });
  }

  async startRoomPlanScan(options = {}) {
    const { roomName, onProgress, onComplete, onError, onRoomDetected } = options;

    this.sessionId = `roomplan_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    this.callbacks = { onProgress, onComplete, onError, onRoomDetected };
    this.isScanning = true;

    return new Promise((resolve) => {
      const sent = this._postMessage(IOS_PROTOCOL.MESSAGES.START_ROOMPLAN, {
        roomName,
        quality: options.quality || 'high',
        captureObjects: options.captureObjects !== false,
      });

      if (sent) {
        resolve({ 
          success: true, 
          sessionId: this.sessionId,
          mode: 'roomplan',
          message: 'RoomPlan scanning started. Move around the room slowly.',
        });
      } else {
        this.isScanning = false;
        resolve({ success: false, error: 'Failed to start RoomPlan' });
      }
    });
  }

  async startLiDARScan(options = {}) {
    const { roomName, onProgress, onComplete, onError } = options;

    this.sessionId = `lidar_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    this.callbacks = { onProgress, onComplete, onError };
    this.isScanning = true;

    return new Promise((resolve) => {
      const sent = this._postMessage(IOS_PROTOCOL.MESSAGES.START_LIDAR_SCAN, {
        roomName,
        quality: options.quality || 'high',
        meshingEnabled: options.meshingEnabled !== false,
        texturesEnabled: options.captureTextures !== false,
      });

      if (sent) {
        resolve({ 
          success: true, 
          sessionId: this.sessionId,
          mode: 'lidar_native',
        });
      } else {
        this.isScanning = false;
        resolve({ success: false, error: 'Failed to start LiDAR scan' });
      }
    });
  }

  captureFrame(metadata = {}) {
    if (!this.isScanning) return { success: false, error: 'No active session' };
    this._postMessage(IOS_PROTOCOL.MESSAGES.CAPTURE_FRAME, { metadata });
    return { success: true, pending: true };
  }

  getProgress() {
    if (!this.isScanning) return null;
    this._postMessage(IOS_PROTOCOL.MESSAGES.GET_PROGRESS);
    return { pending: true };
  }

  async stopScan() {
    if (!this.isScanning) {
      return { success: false, error: 'No active session' };
    }

    return new Promise((resolve) => {
      const originalComplete = this.callbacks.onComplete;
      this.callbacks.onComplete = (data) => {
        originalComplete?.(data);
        resolve({ success: true, ...data });
      };

      this._postMessage(IOS_PROTOCOL.MESSAGES.STOP_SCAN);
    });
  }

  async exportUSDZ() {
    return new Promise((resolve) => {
      window._usdzExportCallback = (data) => {
        resolve(data);
        delete window._usdzExportCallback;
      };

      this._postMessage(IOS_PROTOCOL.MESSAGES.EXPORT_USDZ, {
        callbackId: '_usdzExportCallback',
      });
    });
  }

  cancel() {
    this._postMessage(IOS_PROTOCOL.MESSAGES.CANCEL);
    this.isScanning = false;
    this.sessionId = null;
  }
}


// =============================================================================
// ANDROID NATIVE BRIDGE
// =============================================================================

export class AndroidLiDARBridge {
  constructor() {
    this.sessionId = null;
    this.callbacks = {};
    this.isScanning = false;
    this._setupCallbacks();
  }

  _setupCallbacks() {
    // Android calls these global functions
    window.onAndroidDepthProgress = (data) => {
      this.callbacks.onProgress?.(JSON.parse(data));
    };

    window.onAndroidDepthComplete = (data) => {
      this.isScanning = false;
      this.callbacks.onComplete?.(JSON.parse(data));
    };

    window.onAndroidDepthError = (data) => {
      this.isScanning = false;
      this.callbacks.onError?.(JSON.parse(data));
    };

    window.onAndroidFrameCaptured = (data) => {
      this.callbacks.onFrame?.(JSON.parse(data));
    };
  }

  _callNative(method, ...args) {
    const iface = window[ANDROID_PROTOCOL.INTERFACE_NAME];
    if (iface?.[method]) {
      try {
        return iface[method](...args);
      } catch (e) {
        console.error(`Android native call failed: ${method}`, e);
        return null;
      }
    }
    return null;
  }

  async initialize() {
    const result = this._callNative(ANDROID_PROTOCOL.METHODS.INITIALIZE);
    if (result) {
      try {
        return { success: true, ...JSON.parse(result) };
      } catch {
        return { success: true };
      }
    }
    return { success: false, error: 'Android bridge not available' };
  }

  async startDepthScan(options = {}) {
    const { roomName, onProgress, onComplete, onError, onFrame } = options;

    this.sessionId = `depth_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    this.callbacks = { onProgress, onComplete, onError, onFrame };
    this.isScanning = true;

    const config = JSON.stringify({
      sessionId: this.sessionId,
      roomName: roomName || 'Room',
      quality: options.quality || 'high',
      useDepthAPI: true,
      meshingEnabled: options.meshingEnabled !== false,
    });

    const result = this._callNative(ANDROID_PROTOCOL.METHODS.START_DEPTH_SCAN, config);

    if (result) {
      return {
        success: true,
        sessionId: this.sessionId,
        mode: 'arcore_depth',
      };
    }

    this.isScanning = false;
    return { success: false, error: 'Failed to start ARCore depth scan' };
  }

  captureFrame(metadata = {}) {
    if (!this.isScanning) return { success: false, error: 'No active session' };

    const result = this._callNative(
      ANDROID_PROTOCOL.METHODS.CAPTURE_DEPTH_FRAME,
      this.sessionId,
      JSON.stringify(metadata)
    );

    return result ? { success: true } : { success: false, error: 'Capture failed' };
  }

  async stopScan() {
    if (!this.isScanning) {
      return { success: false, error: 'No active session' };
    }

    return new Promise((resolve) => {
      const originalComplete = this.callbacks.onComplete;
      this.callbacks.onComplete = (data) => {
        originalComplete?.(data);
        resolve({ success: true, ...data });
      };

      this._callNative(ANDROID_PROTOCOL.METHODS.STOP_SCAN, this.sessionId);
    });
  }

  async exportMesh(format = 'obj') {
    const result = this._callNative(
      ANDROID_PROTOCOL.METHODS.EXPORT_MESH,
      this.sessionId,
      format
    );

    if (result) {
      try {
        return { success: true, ...JSON.parse(result) };
      } catch {
        return { success: true, data: result };
      }
    }

    return { success: false, error: 'Export failed' };
  }

  cancel() {
    this._callNative(ANDROID_PROTOCOL.METHODS.CANCEL, this.sessionId);
    this.isScanning = false;
    this.sessionId = null;
  }
}


// =============================================================================
// PHOTOGRAMMETRY SERVICE (WEB FALLBACK)
// =============================================================================

export class PhotogrammetryService {
  constructor() {
    this.capturedImages = [];
    this.roomName = '';
    this.sessionId = null;
    this.metadata = {};
  }

  startSession(roomName, metadata = {}) {
    this.roomName = roomName;
    this.capturedImages = [];
    this.metadata = metadata;
    this.sessionId = `photo_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    return {
      success: true,
      sessionId: this.sessionId,
      mode: 'photogrammetry',
      instructions: this._getInstructions(),
      requirements: {
        minPhotos: 20,
        optimalPhotos: 40,
        overlapPercentage: 60,
      },
    };
  }

  _getInstructions() {
    return [
      { step: 1, text: 'Start from a corner and capture the corner angle', area: 'corners' },
      { step: 2, text: 'Move along each wall, capturing overlapping photos', area: 'walls' },
      { step: 3, text: 'Capture floor and ceiling from center of room', area: 'floor_ceiling' },
      { step: 4, text: 'Photograph any fixtures, outlets, and features', area: 'details' },
      { step: 5, text: 'Add extra angles for any damaged areas', area: 'damage' },
    ];
  }

  async addImage(imageData, imageMetadata = {}) {
    const image = {
      id: `img_${Date.now()}_${this.capturedImages.length}`,
      data: imageData,
      timestamp: new Date().toISOString(),
      roomName: this.roomName,
      index: this.capturedImages.length,
      ...imageMetadata,
    };

    this.capturedImages.push(image);

    const progress = this.getProgress();
    const guidance = this.getGuidance();

    return {
      success: true,
      imageId: image.id,
      totalImages: this.capturedImages.length,
      progress,
      guidance,
    };
  }

  getProgress() {
    const count = this.capturedImages.length;
    const minImages = 20;
    const optimalImages = 40;

    let percentage = 0;
    if (count >= optimalImages) {
      percentage = 100;
    } else if (count >= minImages) {
      percentage = 70 + ((count - minImages) / (optimalImages - minImages)) * 30;
    } else {
      percentage = (count / minImages) * 70;
    }

    return {
      imageCount: count,
      percentage: Math.round(percentage),
      isMinimumMet: count >= minImages,
      isOptimalMet: count >= optimalImages,
      remainingForMinimum: Math.max(0, minImages - count),
      remainingForOptimal: Math.max(0, optimalImages - count),
    };
  }

  getGuidance() {
    const count = this.capturedImages.length;

    const phases = [
      { threshold: 4, status: 'corners', message: 'Capture each corner of the room' },
      { threshold: 8, status: 'walls', message: 'Move along walls with overlapping shots' },
      { threshold: 14, status: 'floor_ceiling', message: 'Capture floor and ceiling from center' },
      { threshold: 20, status: 'details', message: 'Photograph fixtures and features' },
      { threshold: 30, status: 'refinement', message: 'Add detail shots for problem areas' },
      { threshold: Infinity, status: 'complete', message: 'Excellent coverage! Ready for processing.' },
    ];

    const phase = phases.find(p => count < p.threshold) || phases[phases.length - 1];

    return {
      status: phase.status,
      message: phase.message,
      canFinalize: count >= 20,
      qualityEstimate: count >= 40 ? 'high' : count >= 30 ? 'medium' : count >= 20 ? 'acceptable' : 'insufficient',
    };
  }

  async finalize() {
    const progress = this.getProgress();
    const guidance = this.getGuidance();

    return {
      success: true,
      sessionId: this.sessionId,
      roomName: this.roomName,
      mode: 'photogrammetry',
      imageCount: this.capturedImages.length,
      progress,
      guidance,
      images: this.capturedImages.map(img => ({
        id: img.id,
        timestamp: img.timestamp,
        index: img.index,
      })),
      canProcess: this.capturedImages.length >= 20,
      processingEstimate: this._estimateProcessingTime(),
    };
  }

  _estimateProcessingTime() {
    const count = this.capturedImages.length;
    // Rough estimate: 2 seconds per image for server-side processing
    const seconds = count * 2;
    if (seconds < 60) return `${seconds} seconds`;
    return `${Math.round(seconds / 60)} minutes`;
  }

  getImages() {
    return this.capturedImages;
  }

  cancel() {
    this.capturedImages = [];
    this.sessionId = null;
  }
}


// =============================================================================
// UNIFIED CAPTURE MANAGER
// =============================================================================

export class UnifiedCaptureManager {
  constructor() {
    this.iosBridge = null;
    this.androidBridge = null;
    this.photogrammetry = new PhotogrammetryService();
    this.capabilities = null;
    this.activeMode = null;
    this.activeBridge = null;
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized && this.capabilities) {
      return { capabilities: this.capabilities };
    }

    // Get full device capabilities
    this.capabilities = await DeviceCapabilities.getFullCapabilities();

    // Initialize appropriate bridge
    if (this.capabilities.platform.os === 'ios' && this.capabilities.hasNativeBridge) {
      this.iosBridge = new IOSLiDARBridge();
      await this.iosBridge.initialize();
    }

    if (this.capabilities.platform.os === 'android' && this.capabilities.hasNativeBridge) {
      this.androidBridge = new AndroidLiDARBridge();
      await this.androidBridge.initialize();
    }

    this.initialized = true;

    return {
      capabilities: this.capabilities,
      recommendedMode: this.capabilities.recommended3DMode,
    };
  }

  async startCapture(roomName, options = {}) {
    if (!this.initialized) {
      await this.initialize();
    }

    const { forceMode, onProgress, onComplete, onError, onFrame, onRoomDetected } = options;
    const mode = forceMode || this.capabilities.recommended3DMode;
    this.activeMode = mode;

    const callbackOptions = { onProgress, onComplete, onError, onFrame, onRoomDetected, ...options };

    switch (mode) {
      case 'roomplan':
        if (this.iosBridge) {
          this.activeBridge = this.iosBridge;
          return this.iosBridge.startRoomPlanScan({ roomName, ...callbackOptions });
        }
        break;

      case 'lidar_native':
        if (this.iosBridge) {
          this.activeBridge = this.iosBridge;
          return this.iosBridge.startLiDARScan({ roomName, ...callbackOptions });
        }
        break;

      case 'arcore_depth':
        if (this.androidBridge) {
          this.activeBridge = this.androidBridge;
          return this.androidBridge.startDepthScan({ roomName, ...callbackOptions });
        }
        break;

      case 'webxr_guided':
      case 'photo_photogrammetry':
      default:
        this.activeBridge = this.photogrammetry;
        return this.photogrammetry.startSession(roomName, options.metadata);
    }

    // Fallback if preferred mode not available
    this.activeMode = 'photo_photogrammetry';
    this.activeBridge = this.photogrammetry;
    return this.photogrammetry.startSession(roomName, options.metadata);
  }

  async captureFrame(imageData, metadata = {}) {
    if (!this.activeBridge) {
      return { success: false, error: 'No active capture session' };
    }

    if (this.activeMode === 'photo_photogrammetry') {
      return this.photogrammetry.addImage(imageData, metadata);
    }

    if (this.activeBridge.captureFrame) {
      return this.activeBridge.captureFrame(metadata);
    }

    return { success: false, error: 'Capture not supported in this mode' };
  }

  getProgress() {
    if (!this.activeBridge) return null;

    if (this.activeMode === 'photo_photogrammetry') {
      return {
        mode: 'photogrammetry',
        ...this.photogrammetry.getProgress(),
        guidance: this.photogrammetry.getGuidance(),
      };
    }

    if (this.activeBridge.getProgress) {
      return {
        mode: this.activeMode,
        ...this.activeBridge.getProgress(),
      };
    }

    return { mode: this.activeMode, pending: true };
  }

  async finalize() {
    if (!this.activeBridge) {
      return { success: false, error: 'No active capture session' };
    }

    let result;

    if (this.activeMode === 'photo_photogrammetry') {
      result = await this.photogrammetry.finalize();
    } else if (this.activeBridge.stopScan) {
      result = await this.activeBridge.stopScan();
    } else {
      result = { success: false, error: 'Finalize not supported' };
    }

    this.activeMode = null;
    this.activeBridge = null;

    return result;
  }

  async exportMesh(format = 'usdz') {
    if (!this.activeBridge) {
      return { success: false, error: 'No active session' };
    }

    if (this.iosBridge && format === 'usdz') {
      return this.iosBridge.exportUSDZ();
    }

    if (this.androidBridge && this.activeBridge === this.androidBridge) {
      return this.androidBridge.exportMesh(format);
    }

    return { success: false, error: `Export to ${format} not available` };
  }

  cancel() {
    if (this.activeBridge?.cancel) {
      this.activeBridge.cancel();
    }
    this.activeMode = null;
    this.activeBridge = null;
  }

  getCapabilities() {
    return this.capabilities;
  }

  getActiveMode() {
    return this.activeMode;
  }

  isScanning() {
    return this.activeMode !== null;
  }
}


// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const captureManager = new UnifiedCaptureManager();

export default captureManager;
